import React, { useCallback, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Icon from '../../../../atoms/Icon'
import Loading from '../../../../molecules/Loading'
import FlexRow from '../../../../molecules/FlexRow'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import SmartComponentDisplayName from '../../SmartComponentDisplayName'
import useSfClientTasks from './useSfClientTasks'
import WoSfTaskDetailModal from './WoSfTaskDetailModal'
import { SeDebugProvider } from './SeDebugContext'
import SeDebugControl from './SeDebugControl'
import { DEFAULT_COLUMNS } from './constants'
import { getTaskStatus, formatTaskDate, applyFilters } from './helpers'

const SIZE_STYLES = {
  sm: { titleFontSize: '0.875rem', rowPadding: '8px 12px', rowPaddingX: '12px', fontSize: 'var(--font-size-sm)', minHeight: '120px', badgeFontSize: 'var(--font-size-xxs, 0.625rem)' },
  md: { titleFontSize: '1rem', rowPadding: '10px 12px', rowPaddingX: '12px', fontSize: 'var(--font-size-sm)', minHeight: '180px', badgeFontSize: 'var(--font-size-xs)' },
  lg: { titleFontSize: '1.25rem', rowPadding: '12px 12px', rowPaddingX: '12px', fontSize: 'var(--font-size-md)', minHeight: '240px', badgeFontSize: 'var(--font-size-xs)' }
}

const TASK_STATUS_MAP = {
  completed: { icon: 'checkCircle', color: 'var(--color-growth-500)' },
  overdue: { icon: 'clock', color: 'var(--color-decline-500)' },
  outstanding: { icon: 'radioUnchecked', color: 'var(--text-color-subtle)' }
}

const STATUS_FILTER_OPTIONS = [
  { label: 'Open', value: 'open' },
  { label: 'Closed', value: 'closed' },
  { label: 'All', value: 'all' }
]

const STATUS_FILTER_TO_IS_CLOSED = { open: false, closed: true, all: null }

// --- Column renderers ---------------------------------------------------
// Each key maps to { style, render(task, sizes) }. The `subject` column
// uses flex:1; all others are fixed-width so rows stay aligned.

const pillStyle = (sizes) => ({
  fontSize: sizes.badgeFontSize,
  fontWeight: 500,
  padding: '2px 8px',
  borderRadius: '12px',
  backgroundColor: 'var(--color-neutral-200)',
  color: 'var(--text-color-subtle)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
  textAlign: 'center'
})

const fixedCellBase = {
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}

const textCellBase = {
  flexShrink: 0,
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-color-subtle)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}

const boolIndicator = (val) => (
  <Icon
    name={val ? 'checkCircle' : 'radioUnchecked'}
    customSize='16px'
  />
)

const COLUMN_RENDERERS = {
  type: {
    header: 'Type',
    style: { ...fixedCellBase, width: '80px' },
    render: (task, sizes) => task.type
      ? <div style={pillStyle(sizes)}>{task.type}</div>
      : null
  },
  status: {
    header: '',
    style: { ...fixedCellBase, width: '24px' },
    render: (task) => {
      const cfg = TASK_STATUS_MAP[getTaskStatus(task)]
      return (
        <div style={{ color: cfg.color, display: 'flex', alignItems: 'center' }}>
          <Icon name={cfg.icon} customSize='20px' />
        </div>
      )
    }
  },
  subject: {
    header: 'Task',
    style: { flex: 1, minWidth: 0 },
    render: (task, sizes, config) => {
      const showBadge = config?.showHighPriorityBadge !== false
      return (
        <>
          {showBadge && task.isHighPriority ? (
            <div style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-decline-500)',
              fontWeight: 600,
              lineHeight: 1.2
            }}
            >
              High Priority
            </div>
          ) : null}
          <div style={{
            fontSize: sizes.fontSize,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            {task.subject || 'Untitled Task'}
          </div>
        </>
      )
    }
  },
  dueDate: {
    header: 'Due',
    style: { ...textCellBase, width: '110px', textAlign: 'right' },
    render: (task) => {
      const status = getTaskStatus(task)
      const dueStr = formatTaskDate(task.dueDate)
      if (!dueStr) return null
      const color = status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)'
      return <span style={{ color }}>{status === 'completed' ? 'Completed' : `Due ${dueStr}`}</span>
    }
  },
  priority: {
    header: 'Priority',
    style: { ...textCellBase, width: '80px', textAlign: 'center' },
    render: (task) => task.priority || null
  },
  createdDate: {
    header: 'Created',
    style: { ...textCellBase, width: '110px', textAlign: 'right' },
    render: (task) => {
      const str = formatTaskDate(task.createdDate)
      return str || null
    }
  },
  description: {
    header: 'Description',
    style: { ...textCellBase, width: '160px' },
    render: (task) => {
      if (!task.description) return null
      const plain = task.description.replace(/<[^>]*>/g, '')
      return <span title={plain}>{plain.slice(0, 40)}</span>
    }
  },
  codeName: {
    header: 'Code',
    style: { ...fixedCellBase, width: '80px' },
    render: (task, sizes) => task.codeName
      ? <div style={pillStyle(sizes)}>{task.codeName}</div>
      : null
  },
  clientMarkedCompleted: {
    header: 'Marked',
    style: { ...fixedCellBase, width: '52px', color: 'var(--text-color-subtle)' },
    render: (task) => boolIndicator(task.clientMarkedCompleted)
  },
  isHighPriority: {
    header: 'Urgent',
    style: { ...fixedCellBase, width: '52px', color: 'var(--text-color-subtle)' },
    render: (task) => boolIndicator(task.isHighPriority)
  },
  isClosed: {
    header: 'Closed',
    style: { ...fixedCellBase, width: '52px', color: 'var(--text-color-subtle)' },
    render: (task) => boolIndicator(task.isClosed)
  },
  id: {
    header: 'ID',
    style: { ...textCellBase, width: '56px', fontFamily: 'monospace', fontSize: 'var(--font-size-xxs, 0.625rem)' },
    render: (task) => task.id ? String(task.id).slice(-6) : null
  }
}

const columnHeaderStyle = {
  fontSize: 'var(--font-size-xxs, 0.625rem)',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-color-subtle)'
}

function ColumnHeaders ({ columns, sizes }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: `4px ${sizes.rowPaddingX}`,
      borderBottom: '1px solid var(--color-neutral-200)'
    }}
    >
      {columns.map(({ key, header }) => {
        const renderer = COLUMN_RENDERERS[key]
        if (!renderer) return null
        return (
          <div key={key} style={{ ...renderer.style, ...columnHeaderStyle }}>
            {header ?? renderer.header}
          </div>
        )
      })}
      <div style={{ flexShrink: 0, width: '16px' }} />
    </div>
  )
}

ColumnHeaders.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, header: PropTypes.string })).isRequired,
  sizes: PropTypes.object.isRequired
}

// --- Components ----------------------------------------------------------

function StatusFilterPills ({ value, onChange }) {
  return (
    <div style={{ display: 'inline-flex', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--color-neutral-200)' }}>
      {STATUS_FILTER_OPTIONS.map(opt => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            type='button'
            onClick={() => onChange(opt.value)}
            style={{
              padding: '3px 12px',
              fontSize: 'var(--font-size-xs)',
              fontWeight: active ? 600 : 400,
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 150ms ease, color 150ms ease',
              backgroundColor: active ? 'var(--color-primary-500)' : 'transparent',
              color: active ? '#fff' : 'var(--text-color-subtle)'
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

StatusFilterPills.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
}

function WoSfTaskRow ({ task, sizes, columns, config, onClick }) {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderBottom: '1px solid var(--color-neutral-100)',
        padding: sizes.rowPadding,
        cursor: 'pointer',
        borderRadius: '6px',
        transition: 'background-color 150ms ease',
        backgroundColor: hovered ? 'var(--color-neutral-100)' : 'transparent',
        outline: 'none'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {columns.map(({ key }) => {
          const renderer = COLUMN_RENDERERS[key]
          if (!renderer) return null
          return (
            <div key={key} style={renderer.style}>
              {renderer.render(task, sizes, config)}
            </div>
          )
        })}
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          color: 'var(--text-color-subtle)',
          opacity: hovered ? 1 : 0.4,
          transition: 'opacity 150ms ease'
        }}
        >
          <Icon name='chevronRight' customSize='16px' />
        </div>
      </div>
    </div>
  )
}

WoSfTaskRow.propTypes = {
  task: PropTypes.object.isRequired,
  sizes: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, header: PropTypes.string })).isRequired,
  config: PropTypes.object,
  onClick: PropTypes.func.isRequired
}

const LEGACY_FILTER_MAP = { open: 'open', completed: 'closed', all: 'all' }

function WoSfTaskListInner ({ config, sizes, sizeVariant }) {
  const defaultFilter = config?.defaultStatusFilter ??
    LEGACY_FILTER_MAP[config?.completedFilter] ??
    'open'
  const [statusFilter, setStatusFilter] = useState(defaultFilter)
  const isClosed = useMemo(() => STATUS_FILTER_TO_IS_CLOSED[statusFilter] ?? null, [statusFilter])
  const columns = useMemo(() => {
    const raw = Array.isArray(config?.columns) && config.columns.length
      ? config.columns
      : DEFAULT_COLUMNS
    return raw
      .map(entry => {
        if (typeof entry === 'string') return { key: entry, header: null }
        return { key: entry.key, header: entry.header ?? null }
      })
      .filter(({ key }) => COLUMN_RENDERERS[key])
  }, [config?.columns])

  const { tasks, notLinked, isLoading, error } = useSfClientTasks(isClosed)
  const [selectedTaskId, setSelectedTaskId] = useState(null)
  const lastTaskRef = useRef(null)

  const handleCloseDetail = useCallback(() => setSelectedTaskId(null), [])

  const title = config?.title ?? 'Action Items'
  const icon = config?.icon ?? null

  const selectedTask = tasks.find(t => t.id === selectedTaskId) || null
  const modalTask = selectedTask || lastTaskRef.current
  if (selectedTask) {
    lastTaskRef.current = selectedTask
  }

  if (isLoading) {
    return <Loading />
  }

  if (error) {
    return (
      <FadeIn>
        <FlexRow
          className='justify-center items-center'
          style={{ minHeight: sizes.minHeight, color: 'var(--color-decline-500)', fontSize: 'var(--font-size-xs)' }}
        >
          Failed to load tasks
        </FlexRow>
      </FadeIn>
    )
  }

  if (notLinked) {
    return (
      <FadeIn>
        <FlexRow
          className='justify-center items-center'
          style={{ minHeight: sizes.minHeight, color: 'var(--text-color-subtle)', fontSize: 'var(--font-size-xs)' }}
        >
          No tasks available
        </FlexRow>
      </FadeIn>
    )
  }

  const displayTasks = applyFilters(tasks, config)

  return (
    <FadeIn>
      <div style={{ padding: '8px 12px' }}>
        <div style={{
          paddingBottom: '10px',
          marginBottom: '4px',
          borderBottom: '1px solid var(--color-neutral-200)'
        }}
        >
          <SmartComponentDisplayName title={title} icon={icon} size={sizeVariant}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <StatusFilterPills value={statusFilter} onChange={setStatusFilter} />
              <SeDebugControl />
            </div>
          </SmartComponentDisplayName>
        </div>

        {config?.showColumnHeaders && displayTasks.length ? (
          <ColumnHeaders columns={columns} sizes={sizes} />
        ) : null}

        {!displayTasks.length
          ? (
            <FlexRow
              className='justify-center items-center'
              style={{
                minHeight: '80px',
                color: 'var(--text-color-subtle)',
                fontSize: 'var(--font-size-sm)',
                flexDirection: 'column',
                gap: '6px'
              }}
            >
              <Icon name='radioUnchecked' customSize='24px' />
              No tasks found
            </FlexRow>
          )
          : displayTasks.map(task => (
            <WoSfTaskRow
              key={task.id}
              task={task}
              sizes={sizes}
              columns={columns}
              config={config}
              onClick={() => setSelectedTaskId(task.id)}
            />
          ))}

        <WoSfTaskDetailModal
          open={!!selectedTaskId}
          onClose={handleCloseDetail}
          task={modalTask}
        />
      </div>
    </FadeIn>
  )
}

WoSfTaskListInner.propTypes = {
  config: PropTypes.object,
  sizes: PropTypes.object.isRequired,
  sizeVariant: PropTypes.string
}

function WoSfTaskListRenderer ({ config, styleVariant }) {
  const sizes = SIZE_STYLES[styleVariant] || SIZE_STYLES.md

  return (
    <SeDebugProvider config={config}>
      <div style={{ minHeight: sizes.minHeight }}>
        <WoSfTaskListInner
          config={config}
          sizes={sizes}
          sizeVariant={styleVariant}
        />
      </div>
    </SeDebugProvider>
  )
}

WoSfTaskListRenderer.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string
}

export default WoSfTaskListRenderer
